import mixins from 'vue-typed-mixins';

import MathImageMixin, { MATH_IMAGE_RESOURCE_KEY } from '@/user/mixins/MathImageMixin';
import StudyMixin, { STUDY_RESULT } from '@/user/mixins/StudyMixin';
import { TEXT_COLOR } from '@/user/mixins/StudyWorkMixin';
import StudyCalcWorkMixin from '@/user/mixins/StudyCalcWorkMixin';

import UserWindowSizeUtility from '@/classes/common/UserWindowSizeUtility';



/** 数学問題用画像幅 */
const QUESTION_IMAGE_WIDTH = 634;

/** 数学問題用画像高さ */
const QUESTION_IMAGE_HEIGHT = 355;

/** 数学問題用フォントサイズ */
const DEFAULT_MATH_TEXT_SIZE = 14;

/** 数学ルビ用フォントサイズ */
const DEFAULT_MATH_RUBY_SIZE = 7;

/** 数学問題番号用フォントサイズ */
const DEFAULT_MATH_NUMBER_SIZE = 14;


export default mixins(MathImageMixin, StudyMixin, StudyCalcWorkMixin).extend({

  name: 'Calc2Work',

  props: {
    clearSwitch: {
      type: Boolean,
      required: true,
    },
  },

  data(): {
    displayQuestionIndex: number,
    mathTextSize: number;
  } {
    return {
      displayQuestionIndex: -1,
      mathTextSize: 0
    };
  },

  mounted(): void {
    this.initializeCanvas();
    this.$_setMathTextSize();
    this.$_setTextStyle();
  },

  methods: {
    /**
     * 画面サイズ変更検知時のハンドリング
     */
    onChangeWindowSizeHandler(): void {
      // 呼び出し側で処理を実装
      this.setCanvasSize();
      this.$_setMathTextSize();
      this.$_setTextStyle();
      this.$_setQuestion();
    },
    history(feed: number): void {
      const targetIndex = this.displayQuestionIndex + feed;
      if (targetIndex < 0 || this.questions.length < targetIndex + 1) return;
      this.displayQuestionIndex = targetIndex;
      this.$_setQuestion();
    },
    $_clearCanvas(): void {
      this.canvasContext.clearRect(0, 0, this.canvas.width, this.canvas.height);
    },
    $_setMathTextSize(): void {
      this.mathTextSize = UserWindowSizeUtility.calculateRatio(DEFAULT_MATH_TEXT_SIZE);
    },
    $_setTextStyle(): void {
      this.canvasContext.strokeStyle = TEXT_COLOR;
      this.canvasContext.lineWidth   = UserWindowSizeUtility.penSize();
      this.canvasContext.font        = UserWindowSizeUtility.canvasFont(DEFAULT_MATH_TEXT_SIZE);
    },
    $_setMathNumStyle(): void {
      this.canvasContext.strokeStyle = TEXT_COLOR;
      this.canvasContext.lineWidth   = UserWindowSizeUtility.penSize();
      this.canvasContext.font        = UserWindowSizeUtility.canvasFont(DEFAULT_MATH_NUMBER_SIZE);
    },
    async $_setQuestion(isInputClear = false): Promise<void> {
      this.$_clearCanvas(); // まず描画を消しておく

      if (this.displayQuestionIndex === -1) return; // Indexが初期値の場合はreturn

      await this.loadAllImage();
      const questionData = this.questions[this.displayQuestionIndex];
      const question     = questionData.question;
      let   right        = questionData.right;
      const blankType    = questionData.blankType;

      // 問題描画用設定
      this.$_setTextStyle();

      // 画像問題
      if (question.length === 0) {
        this.incrementMediaCount(); // 読み込み画像カウント
        const url = questionData.q;
        const image = new Image();
        image.onload = () => {
          // 画像描画
          if (window.innerWidth <= window.innerHeight) this.drawImage(image, 30, 15);
          else                                         this.drawImage(image, 0, 30);
          // 問題番号の表示
          this.$_drawQuestionNo();
          // 画像読み込み完了処理（タイマースタートもここ）
          this.loadMedia(isInputClear);
        };
        image.width  = QUESTION_IMAGE_WIDTH;
        image.height = QUESTION_IMAGE_HEIGHT;
        image.src    = url.substring(1, url.length - 1);

      // 通常問題
      } else {
        let str = question;

        // 先頭の「#」を省く
        if (str.charAt(0)   === '#') str   = str.substring(1);
        if (right.charAt(0) === '#') right = right.substring(1);

        // 特殊処理
        const index0 = str.indexOf('/*');
        const index1 = str.indexOf('*/');
        if (index0 !== -1 && index1 !== -1) {
          str = str.substring(0, index0) + str.substring(index1 + 2);
        }

        // 「\n」を改行文字に直して文字列を分割
        const strs = str.replace(/\\n/g, '\n').split('\n');

        // Y座標（初期値）
        let posY = 20;

        for (let y = 0; y < strs.length; y++) {
          const count = strs[y].length;
          let   posX  = 28;
          let   i     = 0;

          // 分数フラグ/ルビフラグ
          const pos1 = strs[y].indexOf('[');
          const pos2 = strs[y].indexOf(']', pos1 + 1);
          const pos3 = strs[y].indexOf('/', pos1 + 1);
          const pos4 = strs[y].indexOf('|', pos1 + 1);
          const fractionFlag = pos3 !== -1 && pos3 < pos2;
          const rubyFlag     = pos4 !== -1 && pos4 < pos2;

          // 分数の場合は、標準描画位置を下方修正
          if      (fractionFlag) posY += 10;
          // ルビの場合は、標準描画位置を下方修正（分数とルビの両方含まれる場合は、分数の下方修正だけでよい）
          else if (rubyFlag)     posY += DEFAULT_MATH_RUBY_SIZE;

          while (i < count) {
            const c = strs[y].charAt(i);

            // 分数開始の場合/ルビ開始の場合
            if (c === '[') {
              const endIndex = strs[y].indexOf(']', i + 1);
              // 分数終了がない場合はアラート表示してreturn
              if (endIndex === -1) {
                alert('マスタエラー\nILLEGAL_QUESTION_FRACTION');
                return;
              }

              const posF = strs[y].indexOf('/', i + 1);
              const posR = strs[y].indexOf('|', i + 1);
              if      (posF !== -1 && posF < endIndex)
                posX += this.$_drawMathFraction(strs[y].substring(i + 1, endIndex), posX, posY, true);
              else if (posR !== -1 && posR < endIndex)
                posX += this.$_drawMathRubyChars(strs[y].substring(i + 1, endIndex), posX, posY, true);
              i = endIndex + 1;

            // ルートの場合
            } else if (c === '√') {
              const startIndex = i + 1;
              let   endIndex   = strs[y].indexOf(' ', startIndex);
              if (endIndex === -1) endIndex = strs[y].length;
              posX += this.$_drawMathSqrt(strs[y].substring(startIndex, endIndex), posX, posY, true);
              i = endIndex + 1;

            // それ以外
            } else {
              posX += this.$_drawMathChar(strs[y], i, posX, posY, true);
              // 上付き数字
              if (c === '^') i += 2;
              else           i++;
            }
          }

          // 改行を行う
          if (fractionFlag) posY += 32;
          else              posY += 28;
        }

        // 問題番号の表示
        this.$_drawQuestionNo();
      }
    },
    $_drawMathFraction(str: string, x: number, y: number, drawFlag: boolean): number {
      const uPosY = y - 10;
      const lineY = y + 8;
      const dPosY = y + 9;

      const pos  = str.indexOf('/');
      const uStr = str.substring(0, pos);
      const dStr = str.substring(pos + 1);

      const uWidth = this.$_drawMathFractionWidth(uStr);
      const dWidth = this.$_drawMathFractionWidth(dStr);
      const width = Math.max(uWidth, dWidth);

      const uPosX = x + (width - uWidth) / 2;
      const dPosX = x + (width - dWidth) / 2;

      this.$_drawMathFractionChars(uStr, uPosX, uPosY);
      this.$_drawMathFractionChars(dStr, dPosX, dPosY);

      // 分子と分母の間の線
      this.canvasContext.lineWidth = UserWindowSizeUtility.calculateRatio(1);
      this.drawLine(x, lineY, x + width, lineY);

      return width;
    },
    $_drawMathFractionWidth(str: string): number {
      const count = str.length;
      let   width = 0;
      let   i     = 0;

      while (i < count) {
        const c = str.charAt(i);
        if (c === '√') {
          const startIndex = i + 1;
          let   endIndex   = str.indexOf(' ', startIndex);
          if (endIndex === -1) endIndex = str.length;
          width += this.$_drawMathSqrt(str.substring(startIndex, endIndex), 0, 0, false);
          i = endIndex + 1;
        } else {
          width += this.$_drawMathChar(str, i, 0, 0, false);
          if (c === 'n' || c === '^') i += 2;
          else                        i++;
        }
      }
      return width;
    },
    $_drawMathFractionChars(str: string, x: number, y: number): void {
      const count = str.length;
      let   i     = 0;

      while (i < count) {
        const c = str.charAt(i);
        if (c === '√') {
          const startIndex = i + 1;
          let   endIndex   = str.indexOf(' ', startIndex);
          if (endIndex === -1) endIndex = str.length;
          x += this.$_drawMathSqrt(str.substring(startIndex, endIndex), x, y, true);
          i = endIndex + 1;
        } else {
          x += this.$_drawMathChar(str, i, x, y, true);
          if (c === 'n' || c === '^') i += 2;
          else                        i++;
        }
      }
    },
    $_drawMathSqrt(str: string, x: number, y: number, drawFlag: boolean): number {
      let width = 0;

      // 分数フラグ
      const fractionFlag = str.indexOf('[') !== -1;
      const uPosY        = y - 10;

      // √の描画
      if (fractionFlag) {
        const img = this.getImage(MATH_IMAGE_RESOURCE_KEY.bigroot);
        if (drawFlag) this.drawImage(img, x, uPosY);
        width = img.width / 2;
      } else {
        const img = this.getImage(MATH_IMAGE_RESOURCE_KEY.root);
        if (drawFlag) this.drawImage(img, x, y);
        width = img.width / 2;
      }

      // √書き出し開始位置
      const rootStartPosX = x + width;

      const count = str.length;
      let   i     = 0;
      let   posX  = rootStartPosX;
      const posY  = y;

      while (i < count) {
        const c = str.charAt(i);

        // 分数開始の場合
        if (c === '[') {
          const endIndex = str.indexOf(']', i + 1);
          posX += this.$_drawMathFraction(str.substring(i + 1, endIndex), posX, posY, drawFlag);
          i = endIndex + 1;
        }
        // それ以外
        else {
          posX += this.$_drawMathChar(str, i, posX, posY, drawFlag);
          // 上付き数字
          if (c === '^') i += 2;
          else           i++;
        }
      }

      // √の上線の描画
      if (drawFlag) {
        this.canvasContext.lineWidth = UserWindowSizeUtility.calculateRatio(1);
        if (fractionFlag) this.drawLine(rootStartPosX, uPosY + 1, posX, uPosY + 1);
        else              this.drawLine(rootStartPosX,  posY + 1, posX,  posY + 1);
      }

      return posX - x;
    },
    $_drawMathRubyChars(str: string, x: number, y: number, drawFlag: boolean): number {
      const pos  = str.indexOf('|');
      const dStr = str.substring(0, pos);
      const uStr = str.substring(pos + 1);

      this.canvasContext.font = UserWindowSizeUtility.canvasFont(DEFAULT_MATH_TEXT_SIZE);
      const dWidth = this.canvasContext.measureText(dStr).width;

      this.canvasContext.font = UserWindowSizeUtility.canvasFont(DEFAULT_MATH_RUBY_SIZE);
      const uWidth = this.canvasContext.measureText(uStr).width;

      const width = Math.max(uWidth, dWidth);

      const dPosX = x + (width - dWidth) / 2;
      const uPosX = x + (width - uWidth) / 2;

      // テキストの描画
      this.canvasContext.font = UserWindowSizeUtility.canvasFont(DEFAULT_MATH_TEXT_SIZE);
      this.drawText(dStr, dPosX, y + DEFAULT_MATH_TEXT_SIZE);

      // ルビの描画
      this.canvasContext.font = UserWindowSizeUtility.canvasFont(DEFAULT_MATH_RUBY_SIZE);
      this.drawText(uStr, uPosX, y);

      return width / UserWindowSizeUtility.calculateRatio(1);
    },
    $_drawMathChar(str: string, i: number, x: number, y: number, drawFlag: boolean): number {
      const c       = str.charAt(i);
      let   image   = null;
      let   bigFlag = false;

      // 上付き数字
      if (c === '^') {
        const num = str.charAt(i + 1);
        if      (num === '2') image = this.getImage(MATH_IMAGE_RESOURCE_KEY.u2);
        else if (num === '3') image = this.getImage(MATH_IMAGE_RESOURCE_KEY.u3);
        else if (num === '4') image = this.getImage(MATH_IMAGE_RESOURCE_KEY.u4);
        else if (num === '5') image = this.getImage(MATH_IMAGE_RESOURCE_KEY.u5);
        else if (num === '6') image = this.getImage(MATH_IMAGE_RESOURCE_KEY.u6);
        else if (num === '7') image = this.getImage(MATH_IMAGE_RESOURCE_KEY.u7);
        else if (num === '8') image = this.getImage(MATH_IMAGE_RESOURCE_KEY.u8);
        else if (num === '9') image = this.getImage(MATH_IMAGE_RESOURCE_KEY.u9);
        const prevChar = str.charAt(i - 1);
        if (prevChar === ')' || prevChar === '}') {
          let tmpIndex = i - 2;
          while (true) {
            const tmpc = str.charAt(tmpIndex);
            if      (prevChar === ')' && tmpc === '(') break;
            else if (prevChar === '}' && tmpc === '{') break;
            else if (tmpc === ']') {
              bigFlag = true;
              break;
            }
            tmpIndex--;
          }
        }
      }
      // 小カッコ開始
      else if (c === '(') {
        const endIndex  = str.indexOf(')', i + 1);
        const fracIndex = str.indexOf('[', i + 1);
        if (fracIndex === -1 || endIndex < fracIndex) {
          image = this.getImage(MATH_IMAGE_RESOURCE_KEY.left1);
        } else {
          image = this.getImage(MATH_IMAGE_RESOURCE_KEY.bigleft1);
          bigFlag = true;
        }
      }
      // 中カッコ開始
      else if (c === '{') {
        const endIndex  = str.indexOf('}', i + 1);
        const fracIndex = str.indexOf('[', i + 1);
        if (fracIndex === -1 || endIndex < fracIndex) {
          image = this.getImage(MATH_IMAGE_RESOURCE_KEY.left2);
        } else {
          image = this.getImage(MATH_IMAGE_RESOURCE_KEY.bigleft2);
          bigFlag = true;
        }
      }
      // 小カッコ終了
      else if (c === ')') {
        let tmpIndex = i - 1;
        while (true) {
          const tmpc = str.charAt(tmpIndex);
          if (tmpc === '(') {
            image = this.getImage(MATH_IMAGE_RESOURCE_KEY.right1);
            break;
          } else if (tmpc === ']') {
            image = this.getImage(MATH_IMAGE_RESOURCE_KEY.bigright1);
            bigFlag = true;
            break;
          }
          tmpIndex--;
        }
      }
      // 中カッコ終了
      else if (c === '}') {
        let tmpIndex = i - 1;
        while (true) {
          const tmpc = str.charAt(tmpIndex);
          if (tmpc === '{') {
            image = this.getImage(MATH_IMAGE_RESOURCE_KEY.right2);
            break;
          } else if (tmpc === ']') {
            image = this.getImage(MATH_IMAGE_RESOURCE_KEY.bigright2);
            bigFlag = true;
            break;
          }
          tmpIndex--;
        }
      }
      // その他
      else if (c === '1')  image = this.getImage(MATH_IMAGE_RESOURCE_KEY.n1);
      else if (c === '2')  image = this.getImage(MATH_IMAGE_RESOURCE_KEY.n2);
      else if (c === '3')  image = this.getImage(MATH_IMAGE_RESOURCE_KEY.n3);
      else if (c === '4')  image = this.getImage(MATH_IMAGE_RESOURCE_KEY.n4);
      else if (c === '5')  image = this.getImage(MATH_IMAGE_RESOURCE_KEY.n5);
      else if (c === '6')  image = this.getImage(MATH_IMAGE_RESOURCE_KEY.n6);
      else if (c === '7')  image = this.getImage(MATH_IMAGE_RESOURCE_KEY.n7);
      else if (c === '8')  image = this.getImage(MATH_IMAGE_RESOURCE_KEY.n8);
      else if (c === '9')  image = this.getImage(MATH_IMAGE_RESOURCE_KEY.n9);
      else if (c === '0')  image = this.getImage(MATH_IMAGE_RESOURCE_KEY.n0);
      else if (c === 'a')  image = this.getImage(MATH_IMAGE_RESOURCE_KEY.a);
      else if (c === 'b')  image = this.getImage(MATH_IMAGE_RESOURCE_KEY.b);
      else if (c === 'c')  image = this.getImage(MATH_IMAGE_RESOURCE_KEY.c);
      else if (c === 'x')  image = this.getImage(MATH_IMAGE_RESOURCE_KEY.x);
      else if (c === 'y')  image = this.getImage(MATH_IMAGE_RESOURCE_KEY.y);
      else if (c === 'z')  image = this.getImage(MATH_IMAGE_RESOURCE_KEY.z);
      else if (c === '□') image = this.getImage(MATH_IMAGE_RESOURCE_KEY.blank);
      else if (c === '+')  image = this.getImage(MATH_IMAGE_RESOURCE_KEY.plus);
      else if (c === '-')  image = this.getImage(MATH_IMAGE_RESOURCE_KEY.minus);
      else if (c === '*')  image = this.getImage(MATH_IMAGE_RESOURCE_KEY.times);
      else if (c === '/')  image = this.getImage(MATH_IMAGE_RESOURCE_KEY.div);
      else if (c === '・') image = this.getImage(MATH_IMAGE_RESOURCE_KEY.dot);
      else if (c === '=')  image = this.getImage(MATH_IMAGE_RESOURCE_KEY.equal);
      else if (c === '±')  image = this.getImage(MATH_IMAGE_RESOURCE_KEY.pm);

      if (image !== null) {
        if (bigFlag) y -= 10;
        if (drawFlag) this.drawImage(image, x, y);
      } else {
        this.canvasContext.font = UserWindowSizeUtility.canvasFont(DEFAULT_MATH_TEXT_SIZE);
        if (drawFlag) this.drawText('' + c, x, y + DEFAULT_MATH_TEXT_SIZE);
      }

      return image !== null ? image.width / 2 : this.canvasContext.measureText('' + c).width / UserWindowSizeUtility.calculateRatio(1);
    },
    $_drawQuestionNo(): void {
      this.$_setMathNumStyle();
      this.drawText('(' + (this.displayQuestionIndex + 1) + ')', 4, 16);
    },
  },

  watch: {
    questionIndex: {
      handler(questionIndex: number): void {
        this.displayQuestionIndex = questionIndex;
        this.$_setQuestion();
      }
    },
    // 全消去
    clearSwitch(): void {
      this.$_setQuestion(true);
    },
  }



});
